import { defineStore } from 'pinia';

type AllocationHighlightFunction = () => void;
export type AllocationRegistry = Map<string, AllocationHighlightFunction>;

export const useTimelineStore = defineStore('Timeline', {
    state: () => {
        return {
            allocationRegistry: new Map() as AllocationRegistry,
            referenceAllocationIdByVariantId: new Map() as Map<string, string | null>,
            _variantReferenceCount: new Map() as Map<string, number>,
            locationAllocationTotalCounts: new Map() as Map<string, number>,
        };
    },

    getters: {
        isAllocationRegistered: (state) => (allocationId: string) => {
            return state.allocationRegistry.has(allocationId);
        },
        getReferenceAllocationId: (state) => (variantId: string) => {
            return state.referenceAllocationIdByVariantId.get(variantId) ?? null;
        },
        getAllocationTotalCounts: (state) => {
            return state.locationAllocationTotalCounts;
        },
    },

    actions: {
        registerAllocation(allocationId: string, triggerHighlight: AllocationHighlightFunction) {
            this.allocationRegistry.set(allocationId, triggerHighlight);
        },

        unregisterAllocation(allocationId: string) {
            this.allocationRegistry.delete(allocationId);
        },

        triggerAllocationHighlight(allocationId: string) {
            const highlightFn = this.allocationRegistry.get(allocationId);
            if (highlightFn) {
                highlightFn();
            } else {
                console.warn(`Allocation ${allocationId} not found in registry`);
            }
        },

        jumpToAllocation(allocationId: string) {
            this.triggerAllocationHighlight(allocationId);
        },

        registerVariantReferenceAllocationId(variantId: string, referenceAllocationId: string | null) {
            const count = this._variantReferenceCount.get(variantId) ?? 0;
            this._variantReferenceCount.set(variantId, count + 1);

            // Seed on the first registration only, never overwrite. Later registrations pass
            // `props.allocationVariant.referenceAllocationId`, a copy baked into each allocation when the
            // variant is mapped; the data layer refreshes it after a promotion, but an allocation remounting
            // mid-session must not be able to write over the id `setReferenceAllocationId` put here.
            if (!this.referenceAllocationIdByVariantId.has(variantId)) {
                this.referenceAllocationIdByVariantId.set(variantId, referenceAllocationId);
            }
        },

        unregisterVariantReferenceAllocationId(variantId: string) {
            const count = this._variantReferenceCount.get(variantId) ?? 0;
            const newCount = count - 1;

            if (newCount <= 0) {
                this.referenceAllocationIdByVariantId.delete(variantId);
                this._variantReferenceCount.delete(variantId);
            } else {
                this._variantReferenceCount.set(variantId, newCount);
            }
        },

        setReferenceAllocationId(variantId: string, referenceAllocationId: string | null) {
            this.referenceAllocationIdByVariantId.set(variantId, referenceAllocationId);
        },

        setLocationAllocationTotalCounts(totalCounts: Map<string, number>) {
            this.locationAllocationTotalCounts = totalCounts;
        },

        incrementLocationAllocationTotalCount(id: string) {
            this.locationAllocationTotalCounts.set(id, (this.locationAllocationTotalCounts.get(id) ?? 0) + 1);
        },

        decrementLocationAllocationTotalCount(id: string) {
            this.locationAllocationTotalCounts.set(
                id,
                Math.max(0, (this.locationAllocationTotalCounts.get(id) ?? 0) - 1),
            );
        },
    },
});
